/**
 * Client-side mirror of `api/src/lib/message-format.js`. Same function
 * signature, same output, so the builder's preview panel shows exactly
 * what the recipient will see. Any change here must be reflected there
 * (and vice versa) — the shared behaviour is intentional and small
 * enough that duplication is cheaper than adding a shared package for
 * one file.
 */

type Format = 'plain' | 'markdown'

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const SAFE_URL_RE = /^(https?:\/\/|mailto:)/i
function safeLinkHref(raw: string): string | null {
  const url = raw.trim()
  if (!SAFE_URL_RE.test(url)) return null
  return url.length > 2048 ? null : url
}

function renderInline(escapedLine: string): string {
  return escapedLine
    .replace(/`([^`\n]+)`/g, (_, c) => `<code style="background:#f1f5f9;padding:1px 5px;border-radius:3px;font-family:ui-monospace,Menlo,monospace;font-size:0.92em">${c}</code>`)
    .replace(/\[([^\]\n]+)\]\(([^)\n]+)\)/g, (_m, text, url) => {
      const href = safeLinkHref(url)
      if (!href) return text
      return `<a href="${href}" style="color:#0D9488;text-decoration:underline" target="_blank" rel="noopener noreferrer">${text}</a>`
    })
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>')
}

export function renderMessageBody(body: string | null | undefined, format?: Format): string {
  const src = String(body ?? '')
  if (!src) return ''
  const escaped = escapeHtml(src)
  if (format !== 'markdown') {
    return escaped.replace(/\n/g, '<br/>')
  }

  const lines = escaped.split('\n')
  const out: string[] = []
  let paragraph: string[] = []
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null

  const flushParagraph = () => {
    if (!paragraph.length) return
    const inner = paragraph.map(renderInline).join('<br/>')
    out.push(`<p style="margin:0 0 12px 0">${inner}</p>`)
    paragraph = []
  }
  const flushList = () => {
    if (!list) return
    const items = list.items.map((i) => `<li style="margin:2px 0">${renderInline(i)}</li>`).join('')
    out.push(`<${list.kind} style="margin:0 0 12px 0;padding-left:22px">${items}</${list.kind}>`)
    list = null
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    const bullet = /^(\s*)[-*]\s+(.+)$/.exec(line)
    const numbered = /^(\s*)\d+\.\s+(.+)$/.exec(line)
    if (bullet) {
      flushParagraph()
      if (list?.kind !== 'ul') { flushList(); list = { kind: 'ul', items: [] } }
      list!.items.push(bullet[2] ?? '')
      continue
    }
    if (numbered) {
      flushParagraph()
      if (list?.kind !== 'ol') { flushList(); list = { kind: 'ol', items: [] } }
      list!.items.push(numbered[2] ?? '')
      continue
    }
    if (line.trim() === '') {
      flushList()
      flushParagraph()
      continue
    }
    flushList()
    paragraph.push(line)
  }
  flushList()
  flushParagraph()
  return out.join('')
}

/**
 * Placeholder values used by the preview panel so users can see what
 * the rendered message will look like even before the workflow fires
 * for real. Kept in sync with the backend PREVIEW_TOKENS map.
 */
export const PREVIEW_TOKENS: Record<string, string> = {
  title:              '[Sample WO title]',
  description:        '[Sample description of the issue…]',
  priority:           'high',
  status:             'open',
  source:             'public_qr',
  site_name:          '[Ramanathapuram RAM BRANCH]',
  location_name:      '[Sample Location]',
  asset_name:         '[Sample Asset]',
  submitted_by_name:  '[Joseph]',
  submitted_by_email: 'joseph@example.com',
  reporter_id:        '[uuid]',
  request_url:        'https://cmms.example.com/requests/preview',
  work_order_url:     'https://cmms.example.com/work-orders/preview',
  work_order_id:      '[uuid]',
  wo_code:            'WO-00123',
  task_url:           'https://cmms.example.com/tasks/preview',
  task_id:            '[uuid]',
  task_code:          'TSK-00042',
  trigger_type:       'request.submitted',
}

/**
 * Substitute `{{token}}` occurrences using PREVIEW_TOKENS. Unknown
 * tokens render as a visible marker (`{{typo}}`) so admins can catch
 * misspelled tokens at preview time instead of after the email lands
 * in a real inbox with a blank there.
 */
export function interpolatePreview(template: string): string {
  return String(template ?? '').replace(/\{\{(\w+)\}\}/g, (m, key) => {
    return PREVIEW_TOKENS[key] ?? m
  })
}
